import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Loader, Clock, TrendingUp, Rocket, CheckCircle2, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ProjectCard } from '../components/ProjectCard';
import { PostedVideoCheckModal } from '../components/PostedVideoCheckModal';
import ReportingList from '../components/ReportingList';
import { reportingSupabase } from '../lib/reportingSupabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { differenceInDays } from 'date-fns';

type DashboardRole = 'default' | 'content' | 'ads';

interface DashboardProject {
  id: string;
  release_id: string;
  artist_id: string;
  project_name: string | null;
  release_name: string;
  artist_name: string;
  service_package: string;
  whatsapp_group_id: string | null;
  artist_type: string | null; // 'solo' | 'band' | null
  artist_language: string | null; // 'de' | 'en' | null
  instagram_url: string | null;
  
  // Onboarding
  meta_access_granted: boolean;
  meta_access_date: string | null;
  masterclass_login_created: boolean;
  masterclass_login_date: string | null;
  masterclass_watched: boolean;
  masterclass_watched_date: string | null;
  release_sheet_completed: boolean;
  release_sheet_completed_date: string | null;
  first_four_videos_uploaded: boolean;
  first_four_videos_date: string | null;
  
  // Content Production
  content_call_scheduled: boolean;
  content_call_scheduled_date: string | null;
  content_call_prepared: boolean;
  content_call_prepared_date: string | null;
  content_call_completed: boolean;
  content_call_completed_date: string | null;
  
  // Timeline
  posting_start_date: string | null;
  release_date: string | null;
  campaign_end_date: string | null;
  
  // Follow-up
  feedback_call_scheduled: boolean;
  feedback_call_scheduled_date: string | null;
  feedback_call_completed: boolean;
  feedback_call_completed_date: string | null;
  google_review_requested: boolean;
  google_review_requested_date: string | null;
  google_review_received: boolean;
  google_review_received_date: string | null;
  
  // Links & Counts
  reporting_dashboard_url: string | null;
  song_so_urls: string[] | null;
  spotify_url: string | null;
  meta_campaign_id: string | null;
  meta_campaign_name: string | null;
  video_count: number;
  ad_creative_count: number;
  sheet_count: number;
  
  // Reports
  linked_report_ids: string[] | null;
  notion_page_url: string | null;
  
  // Status
  project_status: 'onboarding' | 'content_production' | 'posting_phase' | 'active_campaign' | 'follow_up' | 'completed' | 'paused';
  ad_budget: number;
  total_budget: number;
  release_status: string;
  notes: string | null;
  created_at: string;
  updated_at: string;

  // Modal acknowledgment state (database-backed, persists across browsers)
  budget_ack_spend: number | null;
  budget_ack_budget: number | null;
  budget_ack_date: string | null;
  lexoffice_ack_amount: number | null;
  report_suggestion_dismissed: boolean | null;
  dismissed_report_ids: string[] | null;

  // Lexoffice invoice (for 3-way reconciliation)
  lexoffice_invoice_id: string | null;

  // Original Monday.com submitted budget (before user adjustments in the tool)
  monday_ad_budget: number | null;

  // Per-platform planned budget splits (in cents)
  planned_budget_meta: number | null;
  planned_budget_tiktok: number | null;
  planned_budget_youtube: number | null;
  planned_budget_follower: number | null;
  planned_budget_additional: number | null;

  // Per-platform ad creative counts
  ad_creative_instagram_count: number;
  ad_creative_tiktok_count: number;
  ad_creative_files_count: number;
}

const ROLE_LABELS: Record<DashboardRole, string> = {
  default: 'Alle',
  content: 'Content',
  ads: 'Ads',
};

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState<DashboardProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [viewMode] = useState<'kanban' | 'timeline'>('kanban');
  const [dashboardView, setDashboardView] = useState<'kanban' | 'reporting'>('kanban');
  const [dashboardRole, setDashboardRole] = useState<DashboardRole>('default');
  const [userDefaultRole, setUserDefaultRole] = useState<DashboardRole>('default');
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(new Set());
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
  const [projectMetrics, setProjectMetrics] = useState<Record<string, { spend: number; budget: number; metaStatus?: string; tiktokStatus?: string }>>({});
  const [mergeModal, setMergeModal] = useState<{ source: DashboardProject; target: DashboardProject } | null>(null);
  const [merging, setMerging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [postedCheckArtist, setPostedCheckArtist] = useState<{ id: string; name: string } | null>(null);

  // Load user's dashboard role from profiles
  useEffect(() => {
    if (!currentUser) {
      setRoleLoading(false);
      return;
    }

    setRoleLoading(true);
    supabase
      .from('profiles')
      .select('dashboard_role')
      .eq('id', currentUser.id)
      .single()
      .then(({ data }) => {
        const role = (data?.dashboard_role as DashboardRole) || 'default';
        setUserDefaultRole(role);
        setDashboardRole(role);
      })
      .finally(() => setRoleLoading(false));
  }, [currentUser]);

  const handleMetricsLoaded = useCallback((projectId: string, spend: number, budget: number, platformStatus?: { meta: string; tiktok: string }) => {
    setProjectMetrics(prev => ({
      ...prev,
      [projectId]: { spend, budget, metaStatus: platformStatus?.meta, tiktokStatus: platformStatus?.tiktok }
    }));

    // Auto-complete check: if both platforms ended, budget ~fully spent, and has linked report
    setProjects(prevProjects => {
      const project = prevProjects.find(p => p.id === projectId);
      if (!project) return prevProjects;
      // Only auto-complete from active_campaign or follow_up
      if (project.project_status !== 'active_campaign' && project.project_status !== 'follow_up') return prevProjects;
      // Must have linked report
      if (!project.linked_report_ids || project.linked_report_ids.length === 0) return prevProjects;
      // Both platforms must be ended/inactive
      const metaEnded = platformStatus?.meta !== 'active';
      const tiktokEnded = platformStatus?.tiktok !== 'active';
      if (!metaEnded || !tiktokEnded) return prevProjects;
      // Budget must be <=1% remaining
      if (budget <= 0) return prevProjects;
      const remaining = budget - spend;
      if (remaining > budget * 0.01) return prevProjects;

      // Auto-move to completed
      console.log(`[Dashboard] Auto-completing project ${project.id} (${project.artist_name} - ${project.release_name}): both platforms ended, ${remaining.toFixed(2)}€ remaining (${(remaining/budget*100).toFixed(1)}%)`);
      supabase
        .from('project_tracking')
        .update({ project_status: 'completed' } as any)
        .eq('id', project.id)
        .then(({ error }) => {
          if (error) {
            console.error('[Dashboard] Auto-complete error:', error);
          } else {
            console.log(`[Dashboard] ✅ Auto-completed: ${project.artist_name} - ${project.release_name}`);
            toast.success(`${project.artist_name} - ${project.release_name} automatisch abgeschlossen`);
          }
        });

      return prevProjects.map(p =>
        p.id === projectId ? { ...p, project_status: 'completed' as const } : p
      );
    });
  }, []);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('dashboard_projects')
        .select('*');

      if (error) throw error;

      const projects = (data as any) || [];

      // Auto-complete: check if all linked reports are 'final' in the reporting DB.
      // Only for active_campaign / follow_up projects with linked_report_ids.
      const candidates = projects.filter((p: DashboardProject) =>
        (p.project_status === 'active_campaign' || p.project_status === 'follow_up') &&
        p.linked_report_ids && p.linked_report_ids.length > 0
      );

      if (candidates.length > 0) {
        // Collect all linked report IDs
        const allReportIds = [...new Set(candidates.flatMap((p: DashboardProject) => p.linked_report_ids!))];

        // Fetch their todo statuses from the reporting DB
        const { data: todos, error: todoError } = await reportingSupabase
          .from('report_todos')
          .select('report_id, status')
          .in('report_id', allReportIds);

        if (!todoError && todos) {
          const todoMap = new Map<string, string>();
          (todos as any[]).forEach((t) => todoMap.set(t.report_id, t.status));

          // For each candidate, check if ALL linked reports are 'final'
          for (const project of candidates) {
            const reportIds = project.linked_report_ids!;
            const allFinal = reportIds.every((id: string) => todoMap.get(id) === 'final');

            if (allFinal) {
              console.log(`[Dashboard] Auto-completing: all ${reportIds.length} report(s) final for ${project.artist_name} - ${project.release_name}`);
              // Update DB
              supabase
                .from('project_tracking')
                .update({ project_status: 'completed' } as any)
                .eq('id', project.id)
                .then(({ error }) => {
                  if (!error) {
                    toast.success(`${project.artist_name} - ${project.release_name} automatisch abgeschlossen (alle Reports final)`);
                  }
                });
              // Update local state
              const idx = projects.findIndex((p: any) => p.id === project.id);
              if (idx >= 0) projects[idx] = { ...projects[idx], project_status: 'completed' };
            }
          }
        }
      }

      setProjects(projects);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Fehler beim Laden der Projekte');
    } finally {
      setLoading(false);
    }
  };

  // Update a single project in local state without reloading all projects.
  // This prevents modal stacks from closing when a modal action is taken.
  const updateProjectInState = async (projectId: string) => {
    try {
      const { data, error } = await supabase
        .from('dashboard_projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) throw error;

      if (data) {
        setProjects(prev => prev.map(p => p.id === projectId ? (data as DashboardProject) : p));
      }
    } catch (error) {
      console.error('Error updating project in state:', error);
    }
  };

  const matchesSearch = (p: DashboardProject) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.artist_name?.toLowerCase().includes(q) ||
      p.release_name?.toLowerCase().includes(q) ||
      p.project_name?.toLowerCase().includes(q)
    );
  };

  const matchesRole = (p: DashboardProject) => {
    if (dashboardRole === 'default') return true;
    if (dashboardRole === 'content') return p.service_package === 'full' || p.service_package === 'content_only';
    if (dashboardRole === 'ads') return p.service_package === 'full' || p.service_package === 'ad_only';
    return true;
  };

  const getUpcomingReleases = () => {
    const now = new Date();
    return projects
      .filter(p => {
        if (!matchesSearch(p) || !matchesRole(p)) return false;
        if (!p.release_date) return false;
        // Don't show as upcoming if the campaign is already running (has spend)
        const metrics = projectMetrics[p.id];
        if (metrics && metrics.spend > 0) return false;
        const releaseDate = new Date(p.release_date);
        const daysUntil = differenceInDays(releaseDate, now);
        // Ads view: only show releases within this week, exclude completed/active
        if (dashboardRole === 'ads') {
          if (['completed', 'active_campaign', 'follow_up'].includes(p.project_status)) return false;
          return daysUntil >= 0 && daysUntil <= 6;
        }
        // Ads Only projects always show in upcoming releases if release date is in the future
        if (p.service_package === 'ad_only' && daysUntil >= 0) return true;
        // Other projects only show if within 7 days
        return daysUntil >= 0 && daysUntil <= 6;
      })
      .sort((a, b) => {
        // Sort by release date (earliest first)
        const dateA = new Date(a.release_date!);
        const dateB = new Date(b.release_date!);
        return dateA.getTime() - dateB.getTime();
      });
  };

  const isUpcomingRelease = (project: DashboardProject) => {
    if (!project.release_date) return false;
    const now = new Date();
    const releaseDate = new Date(project.release_date);
    const daysUntil = differenceInDays(releaseDate, now);
    // Ads view: purely date-based — releases this week only
    if (dashboardRole === 'ads') return daysUntil >= 0 && daysUntil <= 6;
    // Don't treat as upcoming if the campaign is already running (has spend)
    const metrics = projectMetrics[project.id];
    if (metrics && metrics.spend > 0) return false;
    // Ads Only projects always count as upcoming if release date is in the future
    if (project.service_package === 'ad_only' && daysUntil >= 0) return true;
    // Other projects only count as upcoming if within 7 days
    return daysUntil >= 0 && daysUntil <= 6;
  };

  // Check if at least 1 business day has passed since a date
  const hasBusinessDayPassed = (dateStr: string) => {
    const endDate = new Date(dateStr);
    const now = new Date();
    let businessDays = 0;
    const current = new Date(endDate);
    current.setDate(current.getDate() + 1);
    while (current <= now) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) businessDays++;
      if (businessDays >= 1) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  const getProjectsByStatus = (status: string) => {
    // For posting_phase, only show projects that actually have that status
    // The automatic calculation was moved to the database trigger
    // Only Ad Only projects should skip content_production and posting_phase
    const now = new Date();
    const filtered = projects.filter(p => {
      if (!matchesSearch(p) || !matchesRole(p)) return false;

      // --- Ads view: separate filtering logic ---
      if (dashboardRole === 'ads') {
        if (p.service_package === 'content_only') return false;

        // The DB trigger can assign active_campaign/completed too early for
        // ad_only projects. Future release dates must take precedence.
        const releaseDate = p.release_date ? new Date(p.release_date) : null;
        const hasFutureRelease = releaseDate !== null
          && !Number.isNaN(releaseDate.getTime())
          && releaseDate > now;
        const campaignActive = p.project_status === 'active_campaign'
          && releaseDate !== null
          && !Number.isNaN(releaseDate.getTime())
          && releaseDate <= now;
        const isExcluded = ['archived', 'paused'].includes(p.project_status);

        if (status === 'pre_release') {
          // Any future release outside this week's window belongs here,
          // regardless of an accidentally advanced database status.
          if (isExcluded) return false;
          if (hasFutureRelease) return !isUpcomingRelease(p);
          return !campaignActive && !['follow_up', 'completed'].includes(p.project_status) && !isUpcomingRelease(p);
        }
        if (status === 'upcoming_releases') {
          // Any future release in this week's window belongs here,
          // regardless of an accidentally advanced database status.
          if (isExcluded) return false;
          if (hasFutureRelease) return isUpcomingRelease(p);
          return !campaignActive && !['follow_up', 'completed'].includes(p.project_status) && isUpcomingRelease(p);
        }
        if (status === 'active_campaign') {
          return campaignActive;
        }
        if (status === 'completed') {
          // A project with a future release cannot be completed yet.
          return !hasFutureRelease && ['completed', 'follow_up'].includes(p.project_status);
        }
        return false;
      }

      // --- Content view ---
      if (dashboardRole === 'content' && status === 'active_campaign') {
        if (p.project_status === 'active_campaign') return !isUpcomingRelease(p);
        // Content has no dedicated posting_phase column. Once posting has
        // actually started, treat the project as active so it doesn't fall
        // through and disappear from the board entirely.
        if (p.project_status === 'posting_phase') {
          if (!p.posting_start_date) return true;
          return new Date(p.posting_start_date) <= now;
        }
        // Pull in follow_up / completed projects whose campaign ended < 1 business day ago
        if ((p.project_status === 'follow_up' || p.project_status === 'completed') && p.campaign_end_date) {
          return !hasBusinessDayPassed(p.campaign_end_date);
        }
        return false;
      }

      // --- Default view ---
      if (p.project_status !== status || isUpcomingRelease(p)) return false;
      if ((status === 'content_production' || status === 'posting_phase') && p.service_package === 'ad_only') return false;
      return true;
    });

    // Sort active_campaign by budget progress (lowest % first)
    if (status === 'active_campaign') {
      filtered.sort((a, b) => {
        const metricsA = projectMetrics[a.id];
        const metricsB = projectMetrics[b.id];
        const budgetA = a.ad_budget ? a.ad_budget / 100 : 0;
        const budgetB = b.ad_budget ? b.ad_budget / 100 : 0;
        const pctA = metricsA && budgetA > 0 ? metricsA.spend / budgetA : -1;
        const pctB = metricsB && budgetB > 0 ? metricsB.spend / budgetB : -1;
        return pctB - pctA;
      });
    }

    return filtered;
  };

  const toggleColumn = (status: string) => {
    setCollapsedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleDragEnd = () => {
    setDragOverStatus(null);
  };

  const handleDragOver = (e: React.DragEvent, status: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStatus(status);
  };

  const handleDragLeave = () => {
    setDragOverStatus(null);
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData('projectId');
    const currentStatus = e.dataTransfer.getData('currentStatus');

    if (!projectId || currentStatus === newStatus) {
      setDragOverStatus(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('project_tracking')
        .update({ project_status: newStatus } as any)
        .eq('id' as any, projectId as any);

      if (error) throw error;

      toast.success('Projekt-Status aktualisiert');
      await updateProjectInState(projectId);
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error('Fehler beim Aktualisieren des Projekt-Status');
    } finally {
      setDragOverStatus(null);
    }
  };

  const handleCardDrop = (sourceProjectId: string, targetProjectId: string) => {
    if (sourceProjectId === targetProjectId) return;
    const source = projects.find(p => p.id === sourceProjectId);
    const target = projects.find(p => p.id === targetProjectId);
    if (!source || !target) return;
    setMergeModal({ source, target });
  };

  const executeMerge = async () => {
    if (!mergeModal) return;
    setMerging(true);
    const { source, target } = mergeModal;

    try {
      // Merge budgets
      const combinedBudget = (source.ad_budget || 0) + (target.ad_budget || 0);

      // Merge linked_report_ids
      const sourceReports = source.linked_report_ids || [];
      const targetReports = target.linked_report_ids || [];
      const mergedReports = [...new Set([...targetReports, ...sourceReports])];

      // Merge song_so_urls
      const sourceUrls = (source as any).song_so_urls || [];
      const targetUrls = (target as any).song_so_urls || [];
      const mergedUrls = [...new Set([...targetUrls, ...sourceUrls])];

      // Merge notes
      const mergedNotes = [target.notes, source.notes].filter(Boolean).join('\n---\n');

      // Use notion_page_url from whichever has one
      const notionUrl = target.notion_page_url || source.notion_page_url || null;

      // 1. Update target project with merged data
      const { error: updateError } = await supabase
        .from('project_tracking')
        .update({
          ad_budget: combinedBudget,
          linked_report_ids: mergedReports.length > 0 ? mergedReports : null,
          song_so_urls: mergedUrls.length > 0 ? mergedUrls : null,
          notes: mergedNotes || null,
          notion_page_url: notionUrl,
        } as any)
        .eq('id' as any, target.id as any);

      if (updateError) throw updateError;

      // 2. Move source's own release to target project via project_releases
      if (source.release_id) {
        // Check if this release is already linked to target
        const { data: existingLink } = await supabase
          .from('project_releases')
          .select('id')
          .eq('project_id' as any, target.id as any)
          .eq('release_id' as any, source.release_id as any);

        if (!existingLink || existingLink.length === 0) {
          await supabase
            .from('project_releases')
            .insert({
              project_id: target.id,
              release_id: source.release_id,
            } as any);
        }
      }

      // 3. Move all linked releases from source project to target project
      const { data: sourceLinks } = await supabase
        .from('project_releases')
        .select('release_id')
        .eq('project_id' as any, source.id as any);

      if (sourceLinks && sourceLinks.length > 0) {
        for (const link of sourceLinks) {
          // Check if already linked to target
          const { data: exists } = await supabase
            .from('project_releases')
            .select('id')
            .eq('project_id' as any, target.id as any)
            .eq('release_id' as any, (link as any).release_id as any);

          if (!exists || exists.length === 0) {
            await supabase
              .from('project_releases')
              .insert({
                project_id: target.id,
                release_id: (link as any).release_id,
              } as any);
          }
        }

        // Delete source's project_releases links
        await supabase
          .from('project_releases')
          .delete()
          .eq('project_id' as any, source.id as any);
      }

      // 4. Delete source project_tracking
      const { error: deleteError } = await supabase
        .from('project_tracking')
        .delete()
        .eq('id' as any, source.id as any);

      if (deleteError) throw deleteError;

      toast.success(`Projekte zusammengeführt: ${source.artist_name} - ${source.release_name} → ${target.artist_name} - ${target.release_name}`);
      setMergeModal(null);
      loadProjects();
    } catch (error) {
      console.error('Error merging projects:', error);
      toast.error('Fehler beim Zusammenführen der Projekte');
    } finally {
      setMerging(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      pre_release: {
        title: 'Pre-Release',
        icon: Calendar,
        color: 'bg-yellow-500',
        borderColor: 'border-yellow-500/30',
        bgColor: 'bg-yellow-500/10'
      },
      onboarding: {
        title: 'Onboarding',
        icon: Clock,
        color: 'bg-yellow-500',
        borderColor: 'border-yellow-500/30',
        bgColor: 'bg-yellow-500/10'
      },
      content_production: {
        title: 'Content Production',
        icon: Calendar,
        color: 'bg-blue-500',
        borderColor: 'border-blue-500/30',
        bgColor: 'bg-blue-500/10'
      },
      posting_phase: {
        title: 'Posting Phase',
        icon: Rocket,
        color: 'bg-orange-500',
        borderColor: 'border-orange-500/30',
        bgColor: 'bg-orange-500/10'
      },
      active_campaign: {
        title: 'Active Campaign',
        icon: TrendingUp,
        color: 'bg-purple-500',
        borderColor: 'border-purple-500/30',
        bgColor: 'bg-purple-500/10'
      },
      upcoming_releases: {
        title: 'Upcoming Releases',
        icon: Rocket,
        color: 'bg-red-500',
        borderColor: 'border-red-500/30',
        bgColor: 'bg-red-500/10'
      },
      completed: {
        title: 'Completed',
        icon: CheckCircle2,
        color: 'bg-green-500',
        borderColor: 'border-green-500/30',
        bgColor: 'bg-green-500/10'
      },
      paused: {
        title: 'Paused',
        icon: Clock,
        color: 'bg-gray-500',
        borderColor: 'border-gray-500/30',
        bgColor: 'bg-gray-500/10'
      }
    };
    return configs[status as keyof typeof configs] || configs.onboarding;
  };

  const allStatuses = ['onboarding', 'content_production', 'upcoming_releases', 'active_campaign', 'completed'];
  const contentStatuses = ['onboarding', 'content_production', 'active_campaign'];
  const adsStatuses = ['pre_release', 'upcoming_releases', 'active_campaign', 'completed'];
  const statuses = dashboardRole === 'content' ? contentStatuses : dashboardRole === 'ads' ? adsStatuses : allStatuses;

  if (loading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-black p-6">
        <div className="max-w-[1800px] mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-white">Project Dashboard</h1>
              {/* Role filter dropdown */}
              <select
                value={dashboardRole}
                onChange={(e) => {
                  const role = e.target.value as DashboardRole;
                  setDashboardRole(role);
                  if (role !== 'ads') setDashboardView('kanban');
                }}
                className="px-2 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0000FE] cursor-pointer"
              >
                {(Object.keys(ROLE_LABELS) as DashboardRole[]).map(role => (
                  <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                ))}
              </select>
              {/* View switcher — only in ads view */}
              {dashboardRole === 'ads' && (
                <div className="flex items-center bg-gray-900 rounded-lg border border-gray-700 p-0.5">
                  <button
                    onClick={() => setDashboardView('kanban')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      dashboardView === 'kanban'
                        ? 'bg-[#0000FE] text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Kanban
                  </button>
                  <button
                    onClick={() => setDashboardView('reporting')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      dashboardView === 'reporting'
                        ? 'bg-[#0000FE] text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Reporting
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search artists or releases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0000FE] focus:border-transparent w-64"
                />
              </div>
              {dashboardView === 'kanban' && (
                <a
                  href="/new-campaign"
                  className="px-4 py-2 bg-[#0000FE] hover:bg-[#0000dd] text-white rounded-lg text-sm font-medium transition-colors"
                >
                  + Add Release
                </a>
              )}
            </div>
          </div>

          {/* Reporting list view */}
          {dashboardView === 'reporting' && (
            <ReportingList searchQuery={searchQuery} />
          )}



          {/* Kanban Board */}
          {dashboardView === 'kanban' && viewMode === 'kanban' && (
            <div className="grid grid-cols-1 xl:grid-cols-6 gap-4">
              {statuses.map(status => {
                const config = getStatusConfig(status);
                const statusProjects = status === 'upcoming_releases' && dashboardRole !== 'ads' ? getUpcomingReleases() : getProjectsByStatus(status);
                const Icon = config.icon;
                const isCollapsed = collapsedColumns.has(status);

                return (
                  <div 
                    key={status} 
                    className="flex flex-col"
                    onDragOver={(e) => handleDragOver(e, status)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, status)}
                  >
                    {/* Column Header */}
                    <button
                      onClick={() => toggleColumn(status)}
                      className={`${config.bgColor} border ${config.borderColor} rounded-t-lg p-3 mb-2 hover:opacity-90 transition-opacity w-full flex items-center justify-center`}
                      title={`${config.title} (${statusProjects.length})`}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </button>

                    {/* Column Content */}
                    {!isCollapsed && (
                      <div className={`flex-1 space-y-3 min-h-[200px] rounded-b-lg transition-all ${
                        dragOverStatus === status ? 'bg-primary-500/10 border-2 border-primary-500 border-dashed' : ''
                      }`}>
                        {statusProjects.length === 0 ? (
                          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 text-center">
                            <p className="text-gray-500 text-sm">Keine Projekte</p>
                          </div>
                        ) : (
                          statusProjects.map(project => (
                            <ProjectCard
                              key={project.id}
                              project={project}
                              onUpdate={() => updateProjectInState(project.id)}
                              onDragEnd={handleDragEnd}
                              onMetricsLoaded={handleMetricsLoaded}
                              onCardDrop={handleCardDrop}
                              onCheckPostedVideos={(artistId, artistName) => setPostedCheckArtist({ id: artistId, name: artistName })}
                              dashboardRole={dashboardRole}
                            />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Timeline View */}
          {dashboardView === 'kanban' && viewMode === 'timeline' && (
            <div className="bg-gray-900 rounded-lg border border-gray-800 p-6">
              <p className="text-gray-400 text-center">Timeline View - Coming Soon</p>
            </div>
          )}

          {/* Empty State */}
          {dashboardView === 'kanban' && projects.length === 0 && (
            <div className="text-center py-12 bg-gray-900 rounded-lg border border-gray-800">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">Keine Projekte vorhanden</h3>
              <p className="text-gray-500 mb-4">
                Projekte werden automatisch aus Releases erstellt. Erstelle ein Release, um zu beginnen.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Merge Confirmation Modal */}
      {mergeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold text-white mb-4">Projekte zusammenführen?</h2>
            <p className="text-gray-400 text-sm mb-4">
              Das Quellprojekt wird gelöscht und sein Release wird dem Zielprojekt zugeordnet:
            </p>

            <div className="space-y-3 mb-6">
              {/* Source */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <div className="text-xs text-red-400 font-medium mb-1">Wird gelöscht (Quelle)</div>
                <div className="text-white font-semibold">{mergeModal.source.artist_name} - {mergeModal.source.release_name}</div>
                <div className="text-xs text-gray-400 mt-1">
                  Budget: {((mergeModal.source.ad_budget || 0) / 100).toFixed(0)}€
                  {mergeModal.source.linked_report_ids?.length ? ` · ${mergeModal.source.linked_report_ids.length} Report(s)` : ''}
                  {mergeModal.source.notion_page_url ? ' · Notion Sheet' : ''}
                </div>
              </div>

              <div className="text-center text-gray-500">↓ wird zusammengeführt in ↓</div>

              {/* Target */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div className="text-xs text-green-400 font-medium mb-1">Bleibt bestehen (Ziel)</div>
                <div className="text-white font-semibold">{mergeModal.target.artist_name} - {mergeModal.target.release_name}</div>
                <div className="text-xs text-gray-400 mt-1">
                  Budget: {((mergeModal.target.ad_budget || 0) / 100).toFixed(0)}€
                  {mergeModal.target.linked_report_ids?.length ? ` · ${mergeModal.target.linked_report_ids.length} Report(s)` : ''}
                  {mergeModal.target.notion_page_url ? ' · Notion Sheet' : ''}
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3 mb-6 text-xs text-gray-400">
              <div className="font-medium text-gray-300 mb-1">Was passiert:</div>
              <ul className="space-y-1">
                <li>• Budgets werden addiert → {(((mergeModal.source.ad_budget || 0) + (mergeModal.target.ad_budget || 0)) / 100).toFixed(0)}€</li>
                <li>• Reports, URLs und Notizen werden zusammengeführt</li>
                <li>• Quell-Release wird als Linked Release zum Zielprojekt hinzugefügt</li>
                <li>• Alle Linked Releases werden übernommen</li>
                <li>• Quellprojekt wird gelöscht</li>
              </ul>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setMergeModal(null)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                disabled={merging}
              >
                Abbrechen
              </button>
              <button
                onClick={executeMerge}
                disabled={merging}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {merging ? 'Zusammenführen...' : 'Zusammenführen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {postedCheckArtist && (
        <PostedVideoCheckModal
          artistId={postedCheckArtist.id}
          artistName={postedCheckArtist.name}
          onClose={() => setPostedCheckArtist(null)}
          onResolved={() => loadProjects()}
        />
      )}
    </>
  );
};

export default Dashboard;
